import { useCallback, useEffect, useState, KeyboardEvent } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  Chip,
  TextField,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import useNotification from 'src/hooks/useNotification';
import useAuth from 'src/hooks/useAuth';
import { createShares, listResourceShares, revokeShare } from 'src/apis/share';
import { ShareDto } from 'src/types/share';
import { ShareResourceType, toShareResourceTypeApi } from 'src/utils/share';
import {
  LeaveShareModal,
  LeaveShareConfirmState,
} from 'src/modals/LeaveShareModal';

export type { ShareResourceType };

export interface ShareTarget {
  type: ShareResourceType;
  name: string;
  resourceId: string;
}

interface ShareModalProps {
  open: boolean;
  target: ShareTarget | null;
  closeModal: () => void;
  /** Called after create/revoke so sidebars can refresh owned-share badges. */
  onSharesChanged?: () => void;
}

const RESOURCE_LABELS: Record<ShareResourceType, string> = {
  dashboard: 'Dashboard',
  dashboardFolder: 'Dashboard Folder',
  campaign: 'Campaign',
  campaignFolder: 'Campaign Folder',
};

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      marginTop: 8,
    },
    resourceName: {
      color: '#667085',
      marginBottom: 4,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    shareList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      maxHeight: 220,
      overflowY: 'auto',
    },
    shareRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 8,
      border: '1px solid #E4E7EC',
      background: '#F9FAFB',
    },
    shareRowText: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
      flex: 1,
    },
    shareOrgName: {
      color: '#101828',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    shareOrgId: {
      color: '#667085',
      fontSize: 12,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    emptyShares: {
      color: '#667085',
      fontSize: 13,
    },
    pills: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      minHeight: 0,
    },
    pill: {
      backgroundColor: '#EFF8FF',
      color: '#175CD3',
      fontWeight: 500,
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    input: {
      padding: '6px 8px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    helperText: {
      color: '#667085',
      fontSize: 12,
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      '& button': {
        flex: 1,
      },
    },
    loadingRow: {
      display: 'flex',
      justifyContent: 'center',
      padding: 12,
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 480,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

export const ShareModal = ({
  open,
  target,
  closeModal,
  onSharesChanged,
}: ShareModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleSuccess, handleError } = useNotification();
  const { user } = useAuth();

  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingShares, setExistingShares] = useState<ShareDto[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingShareId, setRevokingShareId] = useState<string | null>(null);
  const [revokeConfirm, setRevokeConfirm] =
    useState<LeaveShareConfirmState | null>(null);

  const loadExistingShares = useCallback(async () => {
    if (!target) {
      setExistingShares([]);
      return;
    }

    setLoadingShares(true);
    try {
      const response = await listResourceShares(
        toShareResourceTypeApi(target.type),
        target.resourceId
      );
      setExistingShares(Array.isArray(response?.shares) ? response.shares : []);
    } catch (error) {
      setExistingShares([]);
      handleError(error, 'Unable to load current shares.');
    } finally {
      setLoadingShares(false);
    }
  }, [target, handleError]);

  useEffect(() => {
    if (open) {
      setOrganizationIds([]);
      setInputValue('');
      setLoading(false);
      setRevokingShareId(null);
      setRevokeConfirm(null);
      void loadExistingShares();
    }
  }, [open, target, loadExistingShares]);

  const addOrganizationId = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (organizationIds.includes(trimmed)) {
      setInputValue('');
      return;
    }
    if (trimmed === user?.organizationId) {
      handleError(null, 'You cannot share with your own organization.');
      setInputValue('');
      return;
    }
    if (
      existingShares.some((share) => String(share.organizationId) === trimmed)
    ) {
      handleError(null, 'This organization already has access.');
      setInputValue('');
      return;
    }
    if (organizationIds.length >= 100) {
      handleError(
        null,
        'You can share with at most 100 organizations at once.'
      );
      return;
    }
    setOrganizationIds((prev) => [...prev, trimmed]);
    setInputValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addOrganizationId();
    }
  };

  const handleRemove = (id: string) => {
    setOrganizationIds((prev) => prev.filter((item) => item !== id));
  };

  const handleRevoke = (share: ShareDto) => {
    if (loading || revokingShareId || revokeConfirm) return;

    const orgLabel = share.organizationName || share.organizationId;
    setRevokeConfirm({
      title: 'Remove access',
      message: `Remove access for "${orgLabel}"? They will no longer be able to view this ${
        target ? RESOURCE_LABELS[target.type].toLowerCase() : 'resource'
      }.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setRevokingShareId(share.id);
        try {
          await revokeShare(share.id);
          setExistingShares((prev) =>
            prev.filter((item) => item.id !== share.id)
          );
          handleSuccess(`Removed access for ${orgLabel}.`);
          onSharesChanged?.();
        } catch (error) {
          handleError(error, 'Unable to remove this organization\'s access.');
          throw error;
        } finally {
          setRevokingShareId(null);
        }
      },
    });
  };

  const handleShare = async () => {
    if (!target || loading || !organizationIds.length) return;

    if (organizationIds.length > 100) {
      handleError(
        null,
        'You can share with at most 100 organizations at once.'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await createShares({
        resourceType: toShareResourceTypeApi(target.type),
        resourceId: target.resourceId,
        organizationIds,
      });

      const createdCount = result.created?.length ?? 0;
      const alreadyCount = result.alreadyShared?.length ?? 0;
      const label =
        result.resourceName || target.name || RESOURCE_LABELS[target.type];

      if (createdCount && alreadyCount) {
        handleSuccess(
          `${label} shared with ${createdCount} organization(s). ${alreadyCount} already had access.`
        );
      } else if (createdCount) {
        handleSuccess(`${label} shared successfully.`);
      } else if (alreadyCount) {
        handleSuccess(
          `${label} was already shared with the selected organization(s).`
        );
      } else {
        handleSuccess(`${label} shared successfully.`);
      }

      setOrganizationIds([]);
      setInputValue('');
      await loadExistingShares();
      onSharesChanged?.();
    } catch (error) {
      handleError(
        error,
        'Unable to share. Please check the organization IDs and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || Boolean(revokingShareId) || Boolean(revokeConfirm);
  const canShare = organizationIds.length > 0;
  const title = target ? `Share ${RESOURCE_LABELS[target.type]}` : 'Share';

  return (
    <>
      <Modal
        open={open}
        onClose={busy ? undefined : closeModal}
        BackdropComponent={Backdrop}
        disableEnforceFocus
      >
        <Box sx={modalStyle}>
          <Box className={classes.header}>
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#101828' }}
            >
              {title}
            </Typography>
            <CancelIcon
              style={{
                cursor: busy ? 'default' : 'pointer',
                color: '#667085',
              }}
              onClick={busy ? undefined : closeModal}
            />
          </Box>

          {target?.name ? (
            <Typography
              className={`${textClasses.smRegular} ${classes.resourceName}`}
            >
              {target.name}
            </Typography>
          ) : null}

          <Box className={classes.content}>
            <Box className={classes.section}>
              <Typography className={textClasses.smMedium}>
                Shared with
              </Typography>
              {loadingShares ? (
                <Box className={classes.loadingRow}>
                  <CircularProgress size={22} />
                </Box>
              ) : existingShares.length > 0 ? (
                <Box className={classes.shareList}>
                  {existingShares.map((share) => (
                    <Box key={share.id} className={classes.shareRow}>
                      <Box className={classes.shareRowText}>
                        <Typography
                          className={`${textClasses.smMedium} ${classes.shareOrgName}`}
                        >
                          {share.organizationName || 'Organization'}
                        </Typography>
                        <Typography className={classes.shareOrgId}>
                          {share.organizationId}
                        </Typography>
                      </Box>
                      <IconButton
                        size='small'
                        disabled={busy}
                        onClick={() => handleRevoke(share)}
                        aria-label={`Remove ${
                          share.organizationName || share.organizationId
                        }`}
                      >
                        {revokingShareId === share.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CancelIcon
                            style={{ fontSize: 18, color: '#667085' }}
                          />
                        )}
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography className={classes.emptyShares}>
                  Not shared with any organizations yet.
                </Typography>
              )}
            </Box>

            <Box className={classes.inputField}>
              <Typography className={textClasses.smMedium}>
                Add organization IDs
              </Typography>
              {organizationIds.length > 0 ? (
                <Box className={classes.pills}>
                  {organizationIds.map((id) => (
                    <Chip
                      key={id}
                      label={id}
                      onDelete={busy ? undefined : () => handleRemove(id)}
                      className={classes.pill}
                      size='small'
                    />
                  ))}
                </Box>
              ) : null}
              <TextField
                className={classes.input}
                placeholder='Enter organization ID and press Enter'
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                disabled={busy}
              />
              <Typography className={classes.helperText}>
                Enter one organization ID at a time. Press Enter to confirm each
                ID.
              </Typography>
            </Box>
          </Box>

          <Box className={classes.buttons}>
            <Button
              title='Cancel'
              variant='outlined'
              height={40}
              disabled={busy}
              onClick={closeModal}
            />
            <Button
              title={loading ? 'Sharing…' : 'Share'}
              color='primary'
              height={40}
              disabled={!canShare || busy}
              onClick={handleShare}
            />
          </Box>
        </Box>
      </Modal>
      <LeaveShareModal
        open={!!revokeConfirm}
        confirm={revokeConfirm}
        closeModal={() => setRevokeConfirm(null)}
      />
    </>
  );
};
